import { pick, uniq, uniqBy, isPlainObject, get, omit, isEmpty } from 'lodash'
import { captureSentryException } from '@edulastic/common'
import {
  questionType,
  roleuser,
  collections as collectionConst,
} from '@edulastic/constants'
import { alignmentStandardsFromUIToMongo } from '../../../assessment/utils/helpers'
import gradesMap from '../../Reports/common/static/json/gradesMap.json'
import { isMathTextFieldEmpty } from '../../questionUtils'
import { METADATA_CONTEXT_FLOW } from '../constants/aiQuestionMetadataGeneration'
import {
  allowContentEditCheck,
  allowDuplicateCheck,
  isContentOfCollectionEditable,
} from '../../src/utils/permissionCheck'

const {
  MULTIPLE_CHOICE,
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_DRAG_DROP,
  CLOZE_DROP_DOWN,
  CLOZE_TEXT,
  CLOZE_IMAGE_DRAG_DROP,
  CLOZE_IMAGE_DROP_DOWN,
  CLOZE_IMAGE_TEXT,
  EXPRESSION_MULTIPART,
  EDITING_TASK,
  CHOICE_MATRIX,
  SHORT_TEXT,
  CLASSIFICATION,
  GRAPH,
  AI_QUESTION_ASSISTANCE_ENABLED_QTYPES,
  AI_QUESTION_ASSISTANCE_ENABLED_GRAPH_TYPES,
} = questionType

/**
 * Whether AI question assistance is supported for a given question.
 * Handles graph's per-`graphType`
 * @param {{ type?: string, graphType?: string }} question
 * @returns {boolean}
 */
export const isAIQuestionAssistanceSupported = (question) => {
  if (!question) return false
  const { type, graphType } = question
  if (!AI_QUESTION_ASSISTANCE_ENABLED_QTYPES.includes(type)) return false
  if (type === GRAPH) {
    return AI_QUESTION_ASSISTANCE_ENABLED_GRAPH_TYPES.includes(graphType)
  }
  return true
}

/**
 * Question types that only require stimulus (no options) to generate metadata
 */
const STIMULUS_ONLY_QTYPES = [
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_TEXT,
  CLOZE_IMAGE_TEXT,
  SHORT_TEXT,
  GRAPH,
]

/**
 * Builds tooltip items for a standard using curriculum details
 * @param {Object} standard - Standard object with curriculumId and grades
 * @param {Array} allCurriculums - List of all curriculums
 * @param {boolean} isOutOfPreferredSet - Whether the standard is out of the preferred set
 * @returns {Array<{ label: string, value: string }>} - Tooltip items
 */
export const getStandardTooltipItems = (
  standards,
  curriculum,
  isOutOfPreferredSet = false
) => {
  const curriculumName = curriculum?.curriculum || ''
  const subject = curriculum?.subject || ''
  const grades = uniq(
    (standards.flatMap((s) => s.grades || []) || []).filter(Boolean)
  )
    .slice()
    .sort(
      (a, b) =>
        (gradesMap[a]?.sortKey ?? Infinity) -
        (gradesMap[b]?.sortKey ?? Infinity)
    )

  return [
    curriculumName && { label: 'Standard Set', value: curriculumName },
    subject && { label: 'Subject', value: subject },
    grades.length > 0 && { label: 'Grade', value: grades.join(', ') },
    isOutOfPreferredSet && {
      isNote: true,
      value: `This standard belongs to "${curriculumName}" which isn't currently included in your interested set.`,
    },
  ].filter(Boolean)
}

export const getStandardCurriculumData = (standard, allCurriculums = []) => {
  if (!standard?.curriculumId || !allCurriculums?.length) {
    return {}
  }

  const curriculum = allCurriculums.find(
    (c) => c?._id === standard.curriculumId
  )
  return curriculum
}

export const buildPdlStandardProps = (curriculum, defaultGrades) => {
  const {
    subject,
    pdlCurriculumId,
    pdlAdoptYear,
    pdlCurriculumName = '',
    pdlSubjectLabel = '',
    pdlSubjectId,
    pdlGrades = [],
  } = curriculum || {}

  return {
    pdlCurriculumId,
    pdlAdoptYear,
    pdlCurriculumName,
    pdlSubjectLabel,
    pdlSubjectId,
    pdlGrades,
    grades: defaultGrades,
    subject,
  }
}

const standardFields = [
  '_id',
  'level',
  'grades',
  'identifier',
  'tloIdentifier',
  'tloId',
  'tloDescription',
  'eloId',
  'subEloId',
  'description',
  'curriculumId',
  'pdlId',
]

/**
 * Helper to extract options from object format { responseId: ['Option A', 'Option B'] }
 * @param {Object} optionsObj - Options object
 * @returns {string[]} - Flattened array of option strings
 */
const extractOptionsFromObject = (optionsObj) => {
  if (!isPlainObject(optionsObj)) {
    return []
  }

  return Object.values(optionsObj)
    .flat()
    .filter((opt) => typeof opt === 'string' && opt)
}

const extractOptionsForExpressionMultipart = (question) => {
  const options = extractOptionsFromObject(question.options)
  const validResponse = question?.validation?.validResponse

  if (validResponse) {
    const { dropdown, mathUnits, textinput, value } = validResponse

    if (dropdown && dropdown.value) {
      for (const opt of dropdown.value) {
        if (!opt.value || (opt.value && !opt.value.trim())) {
          return []
        }
      }
    }

    if (mathUnits && mathUnits.value) {
      for (const opt of mathUnits.value) {
        if (isMathTextFieldEmpty(opt.value) || !opt.options.unit) {
          return []
        }
        options.push(opt.value)
      }
    }

    if (textinput && textinput.value) {
      for (const opt of textinput.value) {
        if (!opt.value || (opt.value && !opt.value.trim())) {
          return []
        }
        options.push(opt.value)
      }
    }

    if (value && value.length) {
      for (const opt of value) {
        for (const _opt of opt) {
          if (isMathTextFieldEmpty(_opt.value)) {
            return []
          }
          options.push(_opt.value)
        }
      }
    }
  }

  return options
}

/**
 * Builds the payload for AI metadata generation API based on question type
 * @param {Object} question - Question data object
 * @returns {Object} - { stimulus: string, options: string[] }
 */
export const buildQuestionMetadataPayload = (question) => {
  if (!question) {
    return { stimulus: '', options: [] }
  }

  const { type, stimulus = '' } = question
  let options = []

  switch (type) {
    case MULTIPLE_CHOICE:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CHOICE_MATRIX:
      options = (question.stems || []).filter(Boolean)
      break

    case CLOZE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CLOZE_DROP_DOWN:
      options = extractOptionsFromObject(question.options)
      break

    case CLOZE_IMAGE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.value)
        .filter(Boolean)
      break

    case CLOZE_IMAGE_DROP_DOWN:
      options = (question.options || []).flat().filter(Boolean)
      break

    case EXPRESSION_MULTIPART:
      options = extractOptionsForExpressionMultipart(question)
      break

    case EDITING_TASK:
      options = extractOptionsFromObject(question.options)
      break

    case CLASSIFICATION: {
      const responses = question.groupPossibleResponses
        ? (question.possibleResponseGroups || []).flatMap(
            (g) => g.responses || []
          )
        : question.possibleResponses || []
      options = responses.map((r) => r?.value).filter(Boolean)
      break
    }

    case ESSAY_RICH_TEXT:
    case ESSAY_PLAIN_TEXT:
    case CLOZE_TEXT:
    case CLOZE_IMAGE_TEXT:
    case SHORT_TEXT:
    case GRAPH:
      break

    default:
      break
  }

  return {
    stimulus,
    qType: type,
    ...(options?.length > 0 ? { options } : {}),
  }
}

/**
 * Checks if a question has sufficient content for AI metadata generation
 * based on the question type
 * @param {Object} question - Question data object
 * @returns {boolean} - Whether the question has enough content
 */
export const hasQuestionContentForMetadata = (question) => {
  if (!question) return false

  const { type, stimulus } = question
  const hasStimulusContent =
    typeof stimulus === 'string' && stimulus.trim().length > 0

  if (STIMULUS_ONLY_QTYPES.includes(type)) {
    return hasStimulusContent
  }

  const formattedQuestion = buildQuestionMetadataPayload(question)
  const { options } = formattedQuestion

  const hasOptionsContent = Array.isArray(options) && options.length > 0

  return hasStimulusContent && hasOptionsContent
}

/**
 * @param {Object} params
 * @param {Object} params.suggestedMetaData - Original AI-suggested metadata
 * @param {Object} params.userUpdatedData - User edits that override AI suggestions
 * @returns {{ dok: string|null, difficulty: string|null, standards: Array }}
 */
export const getEffectiveMetadata = ({
  suggestedMetaData = {},
  userUpdatedData = {},
}) => {
  const dok = userUpdatedData.dok || suggestedMetaData?.dok || null
  const difficulty =
    userUpdatedData.difficulty || suggestedMetaData?.difficulty || null
  const standards = userUpdatedData.standards || []

  return { dok, difficulty, standards }
}

/**
 * @param {Array} standards - Selected standards
 * @returns {string|null}
 */
export const getStandardIdsToLog = (standards = []) =>
  (standards || [])
    .map((s) => s._id)
    .filter(Boolean)
    .join(',') || null

export const hasValidStandardSuggestion = (suggestion = {}) =>
  Boolean(suggestion?.standard?._id)

export const hasBulkSuggestion = (suggestion) =>
  Boolean(suggestion?.standard || suggestion?.standards?.length)

export const getItemQuestionIds = (item) =>
  get(item, 'data.questions', [])
    .map((q) => q?.id)
    .filter(Boolean)

export const findItemOnTest = (test, itemId) =>
  (test?.itemGroups || [])
    .flatMap((group) => group.items || [])
    .find((it) => it && typeof it !== 'string' && `${it._id}` === `${itemId}`)

const buildQuestionIdMapForSavedQuestions = (item, savedQuestionIds = []) => {
  if (!item || !savedQuestionIds.length) {
    return {}
  }

  const questionsByPreviousId = get(item, 'data.questions', []).reduce(
    (acc, question) => {
      if (question?.previousQuestionId && question?.id) {
        acc[question.previousQuestionId] = question.id
      }
      return acc
    },
    {}
  )

  return savedQuestionIds.reduce((acc, questionId) => {
    const mappedQuestionId = questionsByPreviousId[questionId]
    if (mappedQuestionId) {
      acc[questionId] = mappedQuestionId
    }
    return acc
  }, {})
}

const resolveQuestionId = (questionId, questionIdMap = {}) =>
  questionIdMap[questionId] || questionId

const buildRemapItemIdMapEntry = ({
  apiItemIdMap = {},
  oldItemId,
  savedQuestionIds = [],
  updatedTest,
}) => {
  const newItemId = apiItemIdMap[oldItemId] || oldItemId
  const updatedItem = findItemOnTest(updatedTest, newItemId)
  const questionIdMap = buildQuestionIdMapForSavedQuestions(
    updatedItem,
    savedQuestionIds
  )

  return {
    newItemId,
    questionIdMap,
  }
}

export const buildRemapItemIdMap = ({
  apiItemIdMap = {},
  items = [],
  updatedTest,
}) =>
  items.reduce((acc, { itemId, questionIds = [] }) => {
    acc[itemId] = buildRemapItemIdMapEntry({
      apiItemIdMap,
      oldItemId: itemId,
      savedQuestionIds: questionIds,
      updatedTest,
    })
    return acc
  }, {})

export const getUpdatedQuestionIds = (questionIds = [], questionIdMap = {}) =>
  questionIds.map((questionId) => resolveQuestionId(questionId, questionIdMap))

export const getStandardsText = (standards = []) => {
  if (standards.length === 1) {
    const [standard] = standards
    return `${standard?.identifier ?? ''}${
      standard?.description ? ` (${standard.description})` : ''
    }`
  }
  return standards
    .map((standard) => standard?.identifier)
    .filter(Boolean)
    .join(', ')
}

/**
 * @param {Object} suggestedMetaData - Original AI-suggested metadata from state
 * @returns {{ dok: string|null, difficulty: string|null, standardIds: string[] }}
 */
export const getFormattedAISuggestedData = (suggestedMetaData = {}) => {
  const { dok = null, difficulty = null, standards = [] } =
    suggestedMetaData || {}
  const standardIds = standards?.map((s) => s._id).filter(Boolean) || []

  return {
    ...(dok && { dok }),
    ...(difficulty && { difficulty }),
    ...(standardIds?.length > 0 && { standardIds }),
  }
}

/**
 * @param {Object} params - Parameters object
 * @param {Object} params.question - Current question data
 * @param {Object} params.metadata - Resolved metadata { dok, difficulty, standards }
 * @param {Array} params.allCurriculums - List of all curriculums from state
 * @param {Array} params.dictAlignments - Dictionaries alignments with standards in UI format
 * @returns {Object} - Updated question with metadata applied
 */
export const applyAIMetadataToQuestion = ({
  question,
  metadata = {},
  allCurriculums,
  dictAlignments,
}) => {
  const { dok, difficulty, standards = [] } = metadata

  if (!standards || standards.length === 0) {
    return {
      ...question,
      ...(dok && { depthOfKnowledge: dok }),
      ...(difficulty && { authorDifficulty: difficulty }),
    }
  }

  const newStandards = standards.map((s) => pick(s, standardFields))
  const curriculumId = newStandards[0]?.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)

  if (!curriculum) {
    captureSentryException(
      new Error('Curriculum not found for suggested standard'),
      {
        errorMessage: 'AI Question Assistance - METADATA: Curriculum not found',
        standards: newStandards,
        curriculumId,
      }
    )
    return question
  }

  const existingAlignments = (question.alignment || []).filter(
    (a) => !a.isEquivalentStandard
  )

  const existingAlignmentIndex = existingAlignments.findIndex(
    (a) => a.curriculumId === curriculumId
  )

  let updatedAlignments
  let questionGrades = []

  if (existingAlignmentIndex !== -1) {
    const dictAlignment = dictAlignments.find(
      (a) => a.curriculumId === curriculumId
    )
    const existingStandards = dictAlignment?.standards || []

    const mergedStandards = [...existingStandards]
    for (const newStd of newStandards) {
      if (!existingStandards.some((s) => s._id === newStd._id)) {
        mergedStandards.push(newStd)
      }
    }

    questionGrades = uniq(
      mergedStandards.flatMap((s) => s.grades || [])
    ).filter(Boolean)

    const updatedDomains = alignmentStandardsFromUIToMongo(mergedStandards)

    updatedAlignments = existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, grades: [], domains: updatedDomains }
        : alignment
    )
  } else {
    questionGrades = uniq(newStandards.flatMap((s) => s.grades || [])).filter(
      Boolean
    )

    const newQuestionAlignment = {
      curriculum: curriculum.curriculum,
      curriculumId,
      subject: curriculum.subject,
      grades: [],
      domains: alignmentStandardsFromUIToMongo(newStandards),
    }
    updatedAlignments = [...existingAlignments, newQuestionAlignment]
  }

  return {
    ...question,
    alignment: updatedAlignments,
    grades: questionGrades,
    subjects: [curriculum.subject].filter(Boolean),
    ...(dok && { depthOfKnowledge: dok }),
    ...(difficulty && { authorDifficulty: difficulty }),
  }
}

/**
 * Resolves the subject + grades to use for standard suggestions using a flow-aware, per-field
 * precedence. Item-level values come from the item/question data (subjects/grades, and — as a
 * fallback — its first authored alignment entry); the dictionaries state slice is not consulted.
 * Subjects and grades are resolved independently, so they may come from different levels.
 *
 * Precedence by flow:
 * - testAuthoring:            Test > Item                (no user-interested fallback)
 * - testBulkMetadataGeneration: Test > User interested     (test review Pear Assist drawer)
 * - itemAuthoring, existing:  Item > User interested
 * - itemAuthoring, new item:  User interested > Item
 *
 * @param {'testAuthoring'|'testBulkMetadataGeneration'|'itemAuthoring'} flow
 * @param {boolean} isNewItem - true when the item has no persisted id yet
 * @param {string[]} testSubjects - test's subjects
 * @param {string[]} testGrades - test's grades
 * @param {string[]} questionSubjects - item subjects (currentQuestion.subjects, or first alignment subject)
 * @param {string[]} questionGrades - item grades (currentQuestion.grades, or first alignment grades)
 * @param {string[]} interestedSubjects - user profile interested subjects
 * @param {string[]} interestedGrades - user profile interested grades
 * @returns {{ subjects: string[], grades: string[] }}
 */
export const resolveMetadataContext = ({
  flow = 'itemAuthoring',
  isNewItem = false,
  testSubjects = [],
  testGrades = [],
  questionSubjects = [],
  questionGrades = [],
  interestedSubjects = [],
  interestedGrades = [],
} = {}) => {
  const firstNonEmpty = (...vals) =>
    vals.find((v) => Array.isArray(v) && v.length) || []

  if (flow === METADATA_CONTEXT_FLOW.TEST_BULK_METADATA_GENERATION) {
    // Test review bulk generate: Test > User interested.
    return {
      subjects: firstNonEmpty(testSubjects, interestedSubjects),
      grades: firstNonEmpty(testGrades, interestedGrades),
    }
  }

  let subjectSources
  let gradeSources
  if (flow === 'testAuthoring') {
    // Test > Item. No fallback to user-interested; missing values are prompted for.
    subjectSources = [testSubjects, questionSubjects]
    gradeSources = [testGrades, questionGrades]
  } else if (isNewItem) {
    // New item in item flow: User interested > Item.
    subjectSources = [interestedSubjects, questionSubjects]
    gradeSources = [interestedGrades, questionGrades]
  } else {
    // Existing item in item flow: Item > User interested.
    subjectSources = [questionSubjects, interestedSubjects]
    gradeSources = [questionGrades, interestedGrades]
  }

  return {
    subjects: firstNonEmpty(...subjectSources),
    grades: firstNonEmpty(...gradeSources),
  }
}

/**
 * Resolves the standard set (curriculumId) to pre-select in the Standard Set dropdown.
 * Precedence: the item's first authored alignment entry's curriculum -> the sole available option.
 * Only returns an id that is present in the provided options.
 *
 * @param {object} question - currentQuestion (reads question.alignment)
 * @param {Array} standardSetOptions - options rendered in the Standard Set dropdown
 * @returns {(string|number|null)} curriculumId to pre-select, or null when none applies
 */
export const resolveStandardSetId = ({
  question,
  standardSetOptions = [],
} = {}) => {
  const options = Array.isArray(standardSetOptions) ? standardSetOptions : []
  const optionIds = new Set(options.map((option) => `${option?._id}`))

  const authoredAlignment = (question?.alignment || []).filter(
    (alignment) => !alignment?.isEquivalentStandard
  )
  const itemCurriculumId = authoredAlignment?.[0]?.curriculumId

  if (itemCurriculumId != null && optionIds.has(`${itemCurriculumId}`)) {
    return itemCurriculumId
  }

  if (options.length === 1) {
    return options[0]._id
  }

  return null
}

/**
 * @param {Array} interestedCurriculums - from getAllInterestedCurriculumsSelector
 * @param {Array} interestedSubjects - from getInterestedSubjectsSelector
 * @returns {{ standardSetOptions: Array }} Options for the standard set dropdown; empty when user has no interested standard sets
 */
export const getStandardSetOptions = ({
  interestedCurriculums,
  interestedSubjects,
}) => {
  try {
    const userInterestedCurriculums = Array.isArray(interestedCurriculums)
      ? interestedCurriculums
      : []
    const userInterestedSubjects = Array.isArray(interestedSubjects)
      ? interestedSubjects
      : []

    if (userInterestedCurriculums.length === 0) {
      return {
        standardSetOptions: [],
      }
    }

    if (userInterestedCurriculums.length === 1) {
      return {
        standardSetOptions: userInterestedCurriculums,
      }
    }

    if (userInterestedSubjects.length === 0) {
      return {
        standardSetOptions: userInterestedCurriculums,
      }
    }

    const _standardSetOptions = userInterestedCurriculums.filter((curriculum) =>
      userInterestedSubjects.includes(curriculum.subject)
    )

    if (_standardSetOptions.length === 1) {
      return {
        standardSetOptions: _standardSetOptions,
      }
    }

    if (_standardSetOptions.length > 1) {
      return {
        standardSetOptions: _standardSetOptions,
      }
    }

    return {
      standardSetOptions: userInterestedCurriculums,
    }
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Question Assistance - METADATA: Error getting standard set options for user curriculum input',
      interestedCurriculums,
      interestedSubjects,
    })

    return {
      standardSetOptions: [],
    }
  }
}

/**
 * Builds updated dictionaries alignments for UI sync.
 * @param {Array} standards - Resolved standards array (from resolveEffectiveMetadata)
 * @param {Array} allCurriculums - List of all curriculums from state
 * @param {Array} existingAlignments - Current alignments from dictionaries state
 * @returns {Array} - Updated alignments array
 */
export const buildDictAlignmentFromStandards = (
  standards,
  allCurriculums,
  existingAlignments = []
) => {
  if (!standards?.length) return existingAlignments

  const newStandards = standards.map((s) => pick(s, standardFields))
  const curriculumId = standards[0]?.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)

  if (!curriculum) {
    captureSentryException(
      new Error('Curriculum not found while building alignment'),
      {
        errorMessage:
          'AI Question Assistance - METADATA: Curriculum not found while building alignment',
        standards: newStandards,
        curriculumId,
      }
    )
    return existingAlignments
  }

  const existingAlignmentIndex = (existingAlignments || []).findIndex(
    (a) => a.curriculumId === curriculumId
  )

  if (existingAlignmentIndex !== -1) {
    const existingStandards =
      existingAlignments?.[existingAlignmentIndex]?.standards || []
    const mergedStandards = [...existingStandards]
    for (const newStd of newStandards) {
      if (!existingStandards.some((s) => s._id === newStd._id)) {
        mergedStandards.push(newStd)
      }
    }

    return existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, standards: mergedStandards }
        : alignment
    )
  }

  return [
    ...existingAlignments,
    {
      curriculum: curriculum.curriculum,
      curriculumId,
      subject: curriculum.subject,
      grades: [],
      standards: newStandards,
    },
  ]
}

/**
 * @param {Object} standard - Standard with `_id`
 * @param {Array<{ standard_id: string|number, confidence_score: number }>|undefined} standardIdsWithConfidence - from API `standard_ids`
 * @returns {number|undefined}
 */
export const getConfidenceScoreForStandard = (
  standard,
  standardIdsWithConfidence
) => {
  try {
    if (!standard?._id || !Array.isArray(standardIdsWithConfidence)) {
      throw new Error('Invalid arguments for confidence score')
    }

    const match = standardIdsWithConfidence.find(
      (item) => `${item?.standard_id}` === `${standard._id}`
    )

    if (!match) {
      return undefined
    }
    if (typeof match.confidence_score === 'number') {
      return match.confidence_score
    }
    throw new Error('Invalid confidence score for standard')
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Question Assistance - METADATA: Error getting confidence score for standard',
      standard,
      standardIdsWithConfidence,
    })
    return undefined
  }
}

/**
 * Selects which of an item's existing standards to surface in Pear Assist,
 * following the product precedence rules:
 *   P1 (exact):     authored standards that belong to the user's preferred set.
 *   P2 (crosswalk): else authored standards' crosswalk (equivalent) standards
 *                   that belong to the preferred set.
 *   P3 (fallback):  else all authored standards (rendered orange since they are
 *                   not in the preferred set).
 *
 * Crosswalk alignment entries are flagged `isEquivalentStandard: true` by the
 * backend. Each returned standard is decorated with `standardSetName` (its
 * alignment entry's curriculum name) and `isOutOfPreferredSet`
 * styling + tooltip.
 *
 * @param {Object} params
 * @param {Array} params.alignments - question.alignment (authored + crosswalk entries)
 * @param {Array<number>} params.interestedCurriculumIds - user's preferred curriculum ids
 * @returns {{ precedence: 'exact'|'crosswalk'|'fallback', standards: Array }}
 */
export const getStandardsByPrecedence = ({
  alignments = [],
  interestedCurriculumIds = [],
} = {}) => {
  const interestedSet = new Set(interestedCurriculumIds)

  const decorateEntryStandards = (entry) => {
    const entryStandards =
      entry?.standards?.length > 0
        ? entry.standards
        : (entry?.domains || []).flatMap((d) => d?.standards || [])

    return (entryStandards || []).map((standard) => {
      const curriculumId = standard?.curriculumId ?? entry?.curriculumId
      return {
        ...standard,
        curriculumId,
        standardSetName: entry?.curriculum || '',
        isOutOfPreferredSet: !interestedSet.has(curriculumId),
      }
    })
  }

  const authoredEntries = (alignments || []).filter(
    (a) => !a?.isEquivalentStandard
  )
  const crosswalkEntries = (alignments || []).filter(
    (a) => a?.isEquivalentStandard
  )

  const authoredStandards = uniqBy(
    authoredEntries.flatMap(decorateEntryStandards),
    '_id'
  )
  const crosswalkStandards = uniqBy(
    crosswalkEntries.flatMap(decorateEntryStandards),
    '_id'
  )

  const authoredInSet = authoredStandards.filter((s) => !s.isOutOfPreferredSet)
  if (authoredInSet.length > 0) {
    return { precedence: 'exact', standards: authoredInSet }
  }

  const crosswalkInSet = crosswalkStandards.filter(
    (s) => !s.isOutOfPreferredSet
  )
  if (crosswalkInSet.length > 0) {
    return { precedence: 'crosswalk', standards: crosswalkInSet }
  }

  return { precedence: 'fallback', standards: authoredStandards }
}

export const isStandardAddedToQuestion = (question = {}) => {
  const alignments = question?.alignment || []
  return alignments.some(
    (alignment) =>
      alignment?.domains?.some((domain) => domain?.standards?.length > 0) ||
      alignment?.standards?.length > 0
  )
}

const QUESTION_SAVE_FIELDS_TO_OMIT = ['qLabel', 'qSubLabel', 'barLabel']

/**
 * Resolves the effective standards to save for a suggestion.
 * Prefers the user-edited `standards` array (set via the metadata editor),
 * falling back to the single AI-suggested `standard`.
 * @param {Object} suggestion - { standard, standards, dok, difficulty }
 * @returns {Array} standards array (may be empty)
 */
export const getEffectiveStandards = (suggestion = {}) => {
  if (Array.isArray(suggestion?.standards)) {
    return suggestion.standards.filter(Boolean)
  }
  return suggestion?.standard ? [suggestion.standard] : []
}

export const buildQuestionForBulkSave = (
  question,
  suggestion,
  allCurriculums
) => {
  const withPearAssistRubric = (q) => {
    if (!suggestion?.rubricApplied) return q
    const rubrics =
      suggestion.rubrics ||
      (suggestion.rubricId
        ? {
            _id: suggestion.rubricId,
            name: suggestion.rubricName || '',
            standardIds: [],
          }
        : null)
    if (!rubrics) return q

    // Prefer Pear Assist suggestion rubric over any existing q.rubrics.
    const next = { ...q, rubrics }
    if (suggestion.rubricMaxScore != null) {
      next.validation = {
        ...(q.validation || {}),
        validResponse: {
          ...(q.validation?.validResponse || {}),
          score: suggestion.rubricMaxScore,
        },
      }
    }
    return next
  }

  if (isStandardAddedToQuestion(question)) {
    return omit(withPearAssistRubric(question), QUESTION_SAVE_FIELDS_TO_OMIT)
  }

  const effectiveStandards = getEffectiveStandards(suggestion)
  if (!effectiveStandards.length) {
    return omit(withPearAssistRubric(question), QUESTION_SAVE_FIELDS_TO_OMIT)
  }

  const questionWithStandard = applyAIMetadataToQuestion({
    question,
    metadata: {
      standards: effectiveStandards,
      dok: suggestion?.dok,
      difficulty: suggestion?.difficulty,
    },
    allCurriculums,
    dictAlignments: [],
  })

  const aiSuggestedMetadata = getFormattedAISuggestedData({
    dok: suggestion?.dok,
    difficulty: suggestion?.difficulty,
    standards: effectiveStandards,
  })

  if (isEmpty(aiSuggestedMetadata)) {
    return omit(
      withPearAssistRubric(questionWithStandard),
      QUESTION_SAVE_FIELDS_TO_OMIT
    )
  }

  return omit(
    withPearAssistRubric({
      ...questionWithStandard,
      aiSuggestedMetadata,
    }),
    QUESTION_SAVE_FIELDS_TO_OMIT
  )
}

/**
 * Whether the current user is an author on the test item (PreviewModal isOwner).
 * Falls back to createdBy when authors is absent or incomplete.
 * @param {{ authors?: Array<{ _id?: string }>, createdBy?: { _id?: string } }} item
 * @param {string} userId
 * @returns {boolean}
 */
export const isItemAuthoredByUser = (item, userId) => {
  if (!item || !userId) return false
  return (
    (item?.authors || []).some((author) => `${author?._id}` === `${userId}`) ||
    `${item?.createdBy?._id}` === `${userId}`
  )
}

/**
 * Premium collection names the user cannot access (PreviewModal premiumCollectionWithoutAccess).
 * @param {Object} item
 * @returns {string[]|false}
 */
export const getPremiumCollectionWithoutAccess = (item) =>
  item?.premiumContentRestriction &&
  item?.collections
    ?.filter(({ type = '' }) => type === collectionConst.types.PREMIUM)
    ?.map(({ name }) => name)

/**
 * Whether the user can edit the item in place (PreviewModal / ReviewItem edit gate).
 * @param {Object} params
 * @param {Object} params.item
 * @param {string} params.userId
 * @param {Array} [params.writableCollections]
 * @param {string} [params.userRole]
 * @param {boolean} [params.isCurator]
 * @returns {boolean}
 */
export const isItemEditableByUser = ({
  item,
  userId,
  writableCollections = [],
  userRole,
  isCurator,
}) => {
  if (!item || !userId) return false

  const premiumCollectionWithoutAccess = getPremiumCollectionWithoutAccess(item)
  const isOwner = isItemAuthoredByUser(item, userId)
  const hasCollectionAccess = allowContentEditCheck(
    item?.collections,
    writableCollections
  )
  const isCollectionContentEditable = isContentOfCollectionEditable(
    item?.collections,
    writableCollections
  )

  if (premiumCollectionWithoutAccess) {
    return false
  }

  return (
    isOwner ||
    userRole === roleuser.EDULASTIC_CURATOR ||
    (hasCollectionAccess && isCurator) ||
    isCollectionContentEditable
  )
}

/**
 * Whether the user may clone the item (PreviewModal allowDuplicate).
 * @param {Object} params
 * @param {Object} params.item
 * @param {string} params.userId
 * @param {Array} [params.userCollections]
 * @returns {boolean}
 */
export const isItemClonableByUser = ({
  item,
  userId,
  userCollections = [],
}) => {
  if (!item || !userId) return false

  const premiumCollectionWithoutAccess = getPremiumCollectionWithoutAccess(item)
  const isOwner = isItemAuthoredByUser(item, userId)
  const allowDuplicatePrimitive = allowDuplicateCheck(
    item?.collections,
    userCollections,
    'item'
  )
  const { derivedFromPremiumBankId = false } = item || {}

  let allowDuplicate =
    (allowDuplicatePrimitive || isOwner) && !derivedFromPremiumBankId

  if (
    premiumCollectionWithoutAccess ||
    (item?.sharedWith?.some(
      (s) =>
        `${s?._id}` === `${item._id}` &&
        s.name === 'LINK' &&
        s.permission === 'NOACTION'
    ) &&
      !isOwner)
  ) {
    allowDuplicate = false
  }

  return allowDuplicate
}

/**
 * Whether a single sub-question is eligible for bulk test metadata generation.
 * @param {Object} params
 * @param {Object} params.item
 * @param {Object} params.question
 * @param {string} params.userId
 * @param {Array} [params.userCollections]
 * @param {Array} [params.writableCollections]
 * @param {string} [params.userRole]
 * @param {boolean} [params.isCurator]
 * @returns {boolean}
 */
export const isQuestionEligibleForTestMetadata = ({
  item,
  question,
  userId,
  userCollections = [],
  writableCollections = [],
  userRole,
  isCurator,
}) =>
  isAIQuestionAssistanceSupported(question) &&
  !isStandardAddedToQuestion(question) &&
  (isItemEditableByUser({
    item,
    userId,
    writableCollections,
    userRole,
    isCurator,
  }) ||
    isItemClonableByUser({ item, userId, userCollections }))

/**
 * First eligible question suggestion for review list row summary (question order).
 * @param {Object} params
 * @param {Object} params.item
 * @param {Object} [params.suggestions]
 * @param {string} params.userId
 * @param {Array} [params.userCollections]
 * @param {Array} [params.writableCollections]
 * @param {string} [params.userRole]
 * @param {boolean} [params.isCurator]
 * @returns {Object|null}
 */
export const getPrimaryReviewSuggestion = ({
  item,
  suggestions = {},
  userId,
  userCollections = [],
  writableCollections = [],
  userRole,
  isCurator,
}) => {
  const questions = item?.data?.questions || []
  for (const q of questions) {
    if (
      !isQuestionEligibleForTestMetadata({
        item,
        question: q,
        userId,
        userCollections,
        writableCollections,
        userRole,
        isCurator,
      })
    )
      continue
    const suggestion = suggestions?.[q?.id]
    if (suggestion?.standard) return suggestion
  }
  return null
}

/**
 * Eligible test items for bulk metadata generation on Test Review.
 * Returns one entry per eligible sub-question; silently excludes unsupported qtypes,
 * aligned questions, and non-owned non-clonable items.
 * @param {Object} params
 * @param {Array} [params.testItems]
 * @param {string} params.userId
 * @param {Array} [params.userCollections]
 * @param {Array} [params.writableCollections]
 * @param {string} [params.userRole]
 * @param {boolean} [params.isCurator]
 * @returns {Array<{ itemId: string, questionId: string, question: Object, payload: Object }>}
 */
export const getEligibleTestMetadataQuestions = ({
  testItems = [],
  userId,
  userCollections = [],
  writableCollections = [],
  userRole,
  isCurator,
}) =>
  (testItems || []).flatMap((item) => {
    const questions = item?.data?.questions || []
    return questions
      .filter((q) =>
        isQuestionEligibleForTestMetadata({
          item,
          question: q,
          userId,
          userCollections,
          writableCollections,
          userRole,
          isCurator,
        })
      )
      .map((q) => ({
        itemId: item._id,
        questionId: q.id,
        question: q,
        payload: buildQuestionMetadataPayload(q),
      }))
  })
